/**
 * Dismissal state for the first-open billing-details prompt (spec #298).
 *
 * "Later" closes it for this visit only, and it comes back on the next one
 * until the details are saved (Shawn 2026-10-30) - same `sessionStorage`
 * reasoning as `passwordNudge.ts`'s own dismiss: it lasts the tab, not the
 * account, and a storage that throws (a private window) reads as "not
 * dismissed" rather than crashing the dashboard.
 *
 * @module web/screens/billingDetailsPrompt
 */

/** The slice of Storage this needs. Injectable, so the tests are not a browser. */
export interface VisitStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const BILLING_DETAILS_PROMPT_DISMISSED_KEY = "billingDetailsPromptDismissed";

/** The session store, or null where there is not one to be had. */
export function browserVisitStore(): VisitStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Was the prompt waved away during this visit? Fails closed: an exception answers `false`, so the prompt shows rather than a white screen. */
export function readBillingDetailsPromptDismissed(store: VisitStore | null): boolean {
  if (!store) return false;
  try {
    return store.getItem(BILLING_DETAILS_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember the dismissal for this visit. Failing to remember is not an error - the component keeps its own state for this render anyway. */
export function rememberBillingDetailsPromptDismissed(store: VisitStore | null): void {
  if (!store) return;
  try {
    store.setItem(BILLING_DETAILS_PROMPT_DISMISSED_KEY, "1");
  } catch {
    // Asking again next render is worse than asking again next visit, but it is not broken.
  }
}
